import React, { useState, useEffect } from 'react';
import { 
  X, 
  Lock, 
  Mail, 
  User, 
  ArrowRight, 
  ArrowLeft, 
  ShieldCheck, 
  AlertCircle, 
  CheckCircle, 
  KeyRound, 
  RefreshCw, 
  Send, 
  Sparkles,
  Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

export default function AuthModal() {
  const { 
    authModalOpen, 
    setAuthModalOpen, 
    authModalTab, 
    setAuthModalTab, 
    login, 
    register, 
    loginWithGoogle, 
    forgotPassword, 
    resetPassword 
  } = useAuth();
  
  // Tab: 'login' | 'register' | 'forgot'
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Forgot Password Flow State
  const [forgotStep, setForgotStep] = useState(1); // 1: enter email | 2: enter OTP & new pass
  const [forgotEmail, setForgotEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');

  // Load Google Client ID from system settings or env
  useEffect(() => {
    const fetchGoogleConfig = async () => {
      try {
        const res = await api.getPublicConfig();
        if (res && res.google_client_id) {
          setGoogleClientId(res.google_client_id.trim());
        }
      } catch (err) {
        // silent
      }
    };
    if (authModalOpen) {
      fetchGoogleConfig();
    }
  }, [authModalOpen]);

  // Load Google Identity Services SDK
  useEffect(() => {
    if (!authModalOpen) return;
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, [authModalOpen]);

  if (!authModalOpen) return null;

  const resetForm = () => {
    setError('');
    setSuccessMsg('');
    setPassword('');
    setConfirmPassword('');
    setOtpCode('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleTabChange = (tab) => {
    setAuthModalTab(tab);
    if (tab === 'forgot') setForgotStep(1);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (authModalTab === 'login') {
      if (!identifier.trim() || !password) {
        setError('Vui lòng nhập tên đăng nhập/email và mật khẩu.');
        return;
      }
      setLoading(true);
      try {
        await login(identifier.trim(), password);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Đăng nhập thất bại.');
      } finally {
        setLoading(false);
      }
    } else if (authModalTab === 'register') {
      if (!username.trim() || !email.trim() || !password) {
        setError('Vui lòng điền đầy đủ các thông tin bắt buộc.');
        return;
      }
      if (password.length < 6) {
        setError('Mật khẩu phải có ít nhất 6 ký tự.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Mật khẩu xác nhận không khớp.');
        return;
      }
      setLoading(true);
      try {
        await register(username.trim(), email.trim(), password);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Đăng ký thất bại.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Forgot Password: Step 1 - Send OTP
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!forgotEmail.trim()) {
      setError('Vui lòng nhập địa chỉ email đã đăng ký tài khoản.');
      return;
    }

    setLoading(true);
    try {
      const res = await forgotPassword(forgotEmail.trim());
      setSuccessMsg(res.message || `Mã xác nhận OTP đã được gửi đến email ${forgotEmail}. (Vui lòng kiểm tra cả Hộp thư đến và mục Thư rác/Spam).`);
      setForgotStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi gửi mã OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password: Step 2 - Reset Password with OTP
  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!otpCode.trim() || !newPassword) {
      setError('Vui lòng nhập đầy đủ mã OTP và mật khẩu mới.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Mật khẩu mới phải có ít nhất 6 ký tự.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Mật khẩu xác nhận không khớp.');
      return;
    }

    setLoading(true);
    try {
      const res = await resetPassword(forgotEmail.trim(), otpCode.trim(), newPassword);
      setSuccessMsg(res.message || 'Đặt lại mật khẩu thành công!');
      setTimeout(() => {
        setAuthModalTab('login');
        setIdentifier(forgotEmail);
        resetForm();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lỗi đặt lại mật khẩu.');
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth Login
  const handleGoogleSignIn = () => {
    setError('');
    if (!googleClientId) {
      setError('Hệ thống chưa cấu hình Google OAuth Client ID trên trang Quản trị (Admin). Vui lòng đăng nhập bằng Email/Mật khẩu.');
      return;
    }
    setGoogleLoading(true);

    try {
      if (window.google?.accounts?.id) {
        // Trigger Google Prompt or One Tap
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            if (response.credential) {
              try {
                await loginWithGoogle(response.credential);
              } catch (err) {
                setError(err.response?.data?.detail || err.message || 'Đăng nhập Google thất bại.');
              } finally {
                setGoogleLoading(false);
              }
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true
        });

        // Prompt Google Account Picker
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            setGoogleLoading(false);
            // Fallback instruction
            setError('Vui lòng cho phép mở popup Google để tiếp tục đăng nhập.');
          }
        });
      } else {
        setGoogleLoading(false);
        setError('Đang tải thư viện Google Sign-In, vui lòng thử lại sau vài giây.');
      }
    } catch (err) {
      setGoogleLoading(false);
      setError('Không thể khởi tạo đăng nhập Google. Vui lòng kiểm tra lại cấu hình.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col font-sans relative">
        
        {/* Top Gradient Banner */}
        <div className="h-1.5 w-full bg-gradient-to-r from-cyan-500 via-amber-400 to-purple-500" />

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <img 
              src="/logo.png" 
              alt="MINO TRADING PRO Logo" 
              className="w-8 h-8 rounded-lg object-contain" 
            />
            <div>
              <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                MINO TRADING PRO
              </h3>
              <p className="text-[11px] text-slate-400">Nền tảng Phân tích Định lượng & AI Đa Khung</p>
            </div>
          </div>
          <button
            onClick={() => setAuthModalOpen(false)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Toggle (Only for Login & Register) */}
        {authModalTab !== 'forgot' ? (
          <div className="flex border-b border-slate-800 bg-slate-950/40 p-1.5 gap-1.5 mx-6 mt-4 rounded-xl border border-slate-800">
            <button
              type="button"
              onClick={() => handleTabChange('login')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                authModalTab === 'login'
                  ? 'bg-cyan-500 text-slate-950 shadow-md'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Đăng Nhập</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('register')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                authModalTab === 'register'
                  ? 'bg-cyan-500 text-slate-950 shadow-md'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
              }`}
            >
              <User className="w-3.5 h-3.5" />
              <span>Đăng Ký Tài Khoản</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mx-6 mt-4 pb-2 border-b border-slate-800">
            <button
              type="button"
              onClick={() => handleTabChange('login')}
              className="text-xs text-slate-400 hover:text-cyan-400 flex items-center gap-1.5 font-bold transition cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Quay lại Đăng nhập</span>
            </button>
            <span className="text-xs font-bold text-amber-300 flex items-center gap-1">
              <KeyRound className="w-3.5 h-3.5" />
              Khôi Phục Mật Khẩu
            </span>
          </div>
        )}

        {/* Modal Body */}
        <div className="p-6 space-y-4">
          
          {/* Notifications */}
          {error && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-start gap-2 animate-fadeIn">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-start gap-2 animate-fadeIn">
              <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* TAB 1: LOGIN */}
          {authModalTab === 'login' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                  Tên Đăng Nhập hoặc Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                    <User className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Nhập tên đăng nhập hoặc email"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                    Mật Khẩu
                  </label>
                  <button
                    type="button"
                    onClick={() => handleTabChange('forgot')}
                    className="text-[11px] text-cyan-400 hover:text-cyan-300 hover:underline font-semibold cursor-pointer"
                  >
                    Quên mật khẩu?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg ${
                  loading
                    ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 shadow-cyan-500/20'
                }`}
              >
                {loading ? (
                  <span>Đang đăng nhập...</span>
                ) : (
                  <>
                    <span>Đăng Nhập Ngay</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Or Divider */}
              <div className="flex items-center gap-3 my-2">
                <div className="h-px bg-slate-800 flex-1" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">hoặc</span>
                <div className="h-px bg-slate-800 flex-1" />
              </div>

              {/* Google Sign-In Button */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                className="w-full py-2.5 px-4 bg-slate-950 hover:bg-slate-800/80 border border-slate-700/80 rounded-xl text-xs font-bold text-slate-200 flex items-center justify-center gap-2.5 transition shadow-sm cursor-pointer hover:border-slate-600"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                <span>{googleLoading ? 'Đang kết nối Google...' : 'Đăng nhập nhanh với Google'}</span>
              </button>
            </form>
          )}

          {/* TAB 2: REGISTER */}
          {authModalTab === 'register' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                  Tên Người Dùng (Username)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                    <User className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Tên tài khoản mong muốn"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                  Địa Chỉ Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@domain.com"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                    Mật Khẩu
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tối thiểu 6 ký tự"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                    Xác Nhận MK
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Nhập lại MK"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg ${
                  loading
                    ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 shadow-cyan-500/20'
                }`}
              >
                {loading ? (
                  <span>Đang tạo tài khoản...</span>
                ) : (
                  <>
                    <span>Tạo Tài Khoản Mới</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Or Divider */}
              <div className="flex items-center gap-3 my-2">
                <div className="h-px bg-slate-800 flex-1" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">hoặc</span>
                <div className="h-px bg-slate-800 flex-1" />
              </div>

              {/* Google Sign-In Button */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                className="w-full py-2.5 px-4 bg-slate-950 hover:bg-slate-800/80 border border-slate-700/80 rounded-xl text-xs font-bold text-slate-200 flex items-center justify-center gap-2.5 transition shadow-sm cursor-pointer hover:border-slate-600"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                <span>Đăng ký nhanh với Google</span>
              </button>
            </form>
          )}

          {/* TAB 3: FORGOT PASSWORD */}
          {authModalTab === 'forgot' && (
            <div className="space-y-4">
              
              {/* Step 1: Request OTP by Email */}
              {forgotStep === 1 && (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div className="p-3.5 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-xs text-cyan-300 space-y-1">
                    <span className="font-bold block text-slate-100 flex items-center gap-1.5">
                      <Mail className="w-4 h-4 text-cyan-400" />
                      Nhận Mã Xác Thực Qua Email
                    </span>
                    <p className="text-slate-300 text-[11px] leading-relaxed">
                      Nhập email đã đăng ký. Hệ thống sẽ gửi một mã OTP gồm 6 chữ số có hiệu lực trong 10 phút để bạn tạo mật khẩu mới.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                      Địa Chỉ Email Đã Đăng Ký
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                        <Mail className="w-4 h-4" />
                      </div>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="email@domain.com"
                        className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition font-mono"
                        autoFocus
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg ${
                      loading
                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 shadow-amber-500/20'
                    }`}
                  >
                    {loading ? (
                      <span>Đang gửi mã OTP...</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Gửi Mã Xác Nhận OTP</span>
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Step 2: Enter OTP & New Password */}
              {forgotStep === 2 && (
                <form onSubmit={handleResetPassword} className="space-y-3.5">
                  <div className="p-3 rounded-xl bg-amber-950/20 border border-amber-500/30 text-xs text-amber-300 flex items-center justify-between">
                    <div>
                      <span className="text-[10.5px] text-slate-400 block">Đang đặt lại MK cho:</span>
                      <strong className="font-mono text-slate-100">{forgotEmail}</strong>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForgotStep(1)}
                      className="text-[10px] text-cyan-400 hover:underline cursor-pointer"
                    >
                      Đổi email khác
                    </button>
                  </div>

                  <div className="p-2.5 rounded-xl bg-cyan-950/40 border border-cyan-500/30 text-[11px] text-cyan-300 flex items-start gap-2">
                    <span className="text-sm shrink-0">📩</span>
                    <span>💡 Nếu không thấy mã trong <strong>Hộp thư đến</strong>, bạn vui lòng kiểm tra cả mục <strong>Thư rác (Spam / Junk)</strong> nhé!</span>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                      Mã Xác Thực OTP (6 Chữ Số)
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="839201"
                      className="w-full bg-slate-950 border border-amber-500/60 rounded-xl px-3 py-2.5 text-center text-base font-mono font-black text-amber-300 tracking-[8px] placeholder-slate-600 focus:outline-none focus:border-amber-400 transition"
                      autoFocus
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="space-y-1">
                      <label className="text-[10.5px] font-bold text-slate-300 uppercase tracking-wider block">
                        Mật Khẩu Mới
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Tối thiểu 6 ký tự"
                        className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10.5px] font-bold text-slate-300 uppercase tracking-wider block">
                        Xác Nhận MK Mới
                      </label>
                      <input
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Nhập lại MK"
                        className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg ${
                      loading
                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 shadow-emerald-500/20'
                    }`}
                  >
                    {loading ? (
                      <span>Đang cập nhật...</span>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Đặt Lại Mật Khẩu Ngay</span>
                      </>
                    )}
                  </button>

                  <div className="text-center pt-1">
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="text-[11px] text-slate-400 hover:text-amber-300 transition cursor-pointer"
                    >
                      Chưa nhận được mã? <strong className="text-amber-400 underline">Gửi lại OTP</strong>
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-950/60 border-t border-slate-800 text-[11px] text-slate-400 flex items-center justify-between">
          <span>Hệ thống Phân tích Định lượng</span>
          <span className="text-cyan-400 font-bold">MINO TRADING PRO</span>
        </div>
      </div>
    </div>
  );
}
